// src/components/PrecottagesByNumber.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  Copy,
  FileText,
  Hash,
  Loader2,
  Package,
  Truck,
  X,
  DollarSign,
  ArrowLeft,
} from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import { LazyLoading } from "./ui/LazyLoading";
import { useNavigate, useParams } from "react-router-dom";

const cx = (...c) => c.filter(Boolean).join(" ");

const formatUSD = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
};

const safeText = (v) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const StatusBadge = ({ status }) => {
  const s = safeText(status);
  const isSuccess =
    s.includes("ارسال") || s.includes("ارسال شده") || s.includes("تایید");
  return (
    <span
      className={cx(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-extrabold",
        isSuccess
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-sky-50 text-sky-700 border-sky-200"
      )}
    >
      <CheckCircle2 size={14} />
      {s}
    </span>
  );
};

const Card = ({ title, icon: Icon, className = "", actions, children }) => {
  return (
    <div
      className={cx(
        "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden",
        className
      )}
      dir="ltr"
    >
      <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        {/* ✅ actions LEFT, title RIGHT */}
        <div className="flex items-center justify-between gap-3">
          {/* Left side (actions) */}
          <div className="flex items-center gap-2">{actions}</div>

          {/* Right side (title) */}
          <div className="flex items-center gap-3">
            <h3 className="text-base sm:text-lg font-extrabold text-slate-800 text-right">
              {title}
            </h3>
            {Icon ? (
              <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                <Icon size={20} />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="p-5">{children}</div>
    </div>
  );
};

const InfoTile = ({
  icon: Icon,
  label,
  value,
  copyable = false,
  copied,
  onCopy,
}) => {
  const v = safeText(value);
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm hover:shadow-md transition">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={copyable ? () => onCopy(v, label) : undefined}
          className={cx(
            "inline-flex items-center justify-center rounded-xl p-2 transition",
            copyable ? "hover:bg-white" : "cursor-default"
          )}
          disabled={!copyable}
          aria-label={copyable ? `کپی ${label}` : undefined}
        >
          {copyable ? (
            copied === label ? (
              <CheckCircle2 className="text-emerald-600" size={18} />
            ) : (
              <Copy className="text-slate-400" size={18} />
            )
          ) : (
            <span className="text-slate-300" />
          )}
        </button>

        <div className="ml-auto text-right">
          <div className="flex items-center justify-end gap-2 text-xs font-bold text-slate-600">
            <span>{label}</span>
            {Icon ? <Icon className="text-slate-400" size={16} /> : null}
          </div>
          <div className="mt-2 text-sm sm:text-base font-extrabold text-slate-900 break-all">
            {v}
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryBox = ({ label, value, icon: Icon }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition">
    <div className="flex items-center justify-between gap-3">
      <div className="text-right">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="mt-1 text-lg font-extrabold text-slate-900">
          {safeText(value)}
        </div>
      </div>
      {Icon ? (
        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
          <Icon size={20} />
        </div>
      ) : null}
    </div>
  </div>
);

const PrecottagesByNumber = () => {
  const navigate = useNavigate();
  const { "co-code": coCode } = useParams();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedField, setCopiedField] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const titleNumber = useMemo(() => safeText(data?.cottage_number), [data]);

  const fetchDetail = async () => {
    if (!coCode) return;
    try {
      setErrorMsg("");
      setIsLoading(true);

      // Adjust this endpoint if your backend requires trailing slash or different path.
      const res = await axiosInstance.get(`/precottages/${coCode}/`);
      setData(res.data || null);
    } catch (err) {
      console.error("Error fetching precottage detail:", err);
      setData(null);
      setErrorMsg("خطا در دریافت اطلاعات منشأ ارز.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coCode]);

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(String(text ?? ""));
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 1500);
    } catch (e) {
      console.error("Clipboard error:", e);
    }
  };

  return (
    <div className="mt-10 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-28 px-3 sm:px-6 font-vazirmatn">
      <div dir="rtl" className="max-w-6xl mx-auto">
        {/* Header container */}
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden">
          <div className="px-5 sm:px-7 py-5 border-b  border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-row-reverse  gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
              >
                بازگشت
                <ArrowLeft size={18} />
              </button>

              <div className="ml-auto flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <BadgeCheck size={22} />
                </div>

                <div className="text-right">
                  <h2 className="text-lg sm:text-xl font-extrabold text-slate-800">
                    جزئیات منشأ ارز
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    کد یکتای منشا ارز{" "}
                    <span className="font-bold text-slate-700">
                      {safeText(coCode)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6">
            {isLoading ? (
              <div className="flex justify-center py-14">
                <LazyLoading />
              </div>
            ) : errorMsg ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-right">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-extrabold text-rose-700">
                      خطا
                    </div>
                    <div className="text-sm text-rose-700 mt-1">{errorMsg}</div>
                  </div>
                  <button
                    type="button"
                    onClick={fetchDetail}
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-rose-700 bg-white hover:bg-rose-50 border border-rose-200 transition"
                  >
                    <Loader2 size={16} />
                    تلاش دوباره
                  </button>
                </div>
              </div>
            ) : !data ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-right text-slate-700">
                منشأ ارزی یافت نشد.
              </div>
            ) : (
              <>
                {/* Top summary / status */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                  <div className="text-right">
                    <div className="text-xs text-slate-500">شماره کوتاژ</div>
                    <div className="text-lg font-extrabold text-slate-900">
                      {titleNumber}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      شماره مرجع:{" "}
                      <span className="font-bold text-slate-700">
                        {safeText(data.refrence_number)}
                      </span>
                    </div>
                  </div>

                  <StatusBadge status={data.co_status || ""} />
                </div>

                {/* Cards grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Card
                    title="اطلاعات اصلی"
                    icon={Hash}
                    className="lg:col-span-2"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InfoTile
                        icon={FileText}
                        label="شماره پروفرما"
                        value={data.proforma_order_no}
                        copyable
                        copied={copiedField}
                        onCopy={copyToClipboard}
                      />
                      <InfoTile
                        icon={Package}
                        label="کد VCode پروفرما"
                        value={data.proforma_vcode}
                        copyable
                        copied={copiedField}
                        onCopy={copyToClipboard}
                      />
                      <InfoTile
                        icon={Hash}
                        label="کد یکتای منشا ارز"
                        value={data.co_code}
                        copyable
                        copied={copiedField}
                        onCopy={copyToClipboard}
                      />
                      <InfoTile
                        icon={FileText}
                        label="شماره مرجع"
                        value={data.refrence_number}
                        copyable
                        copied={copiedField}
                        onCopy={copyToClipboard}
                      />
                      <InfoTile
                        icon={CalendarDays}
                        label="تاریخ CO"
                        value={data.co_date}
                      />
                      <InfoTile
                        icon={CalendarDays}
                        label="تاریخ ترخیص"
                        value={data.co_clearence_date}
                      />
                    </div>
                  </Card>

                  <Card title="مالی" icon={DollarSign}>
                    <div className="space-y-3">
                      <SummaryBox
                        label="ارزش کل"
                        value={formatUSD(data.total_value)}
                        icon={DollarSign}
                      />
                      <SummaryBox
                        label="FOB"
                        value={formatUSD(data.co_fob)}
                        icon={DollarSign}
                      />
                      <SummaryBox
                        label="هزینه حمل"
                        value={formatUSD(data.co_freight)}
                        icon={Truck}
                      />
                    </div>
                  </Card>
                </div>

                {/* Optional: link to cottage if present */}
                {data.cottage_number ? (
                  <div className="mt-4">
                    <Card
                      title="دسترسی سریع"
                      icon={FileText}
                      actions={
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/cottages/${data.cottage_number}`)
                          }
                          className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
                        >
                          مشاهده کوتاژ
                        </button>
                      }
                    >
                      <div className="text-sm text-slate-700 text-right">
                        برای مشاهده جزئیات کوتاژ مرتبط، از دکمه «مشاهده کوتاژ»
                        استفاده کنید.
                      </div>
                    </Card>
                  </div>
                ) : null}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 sm:px-7 py-4 border-t border-slate-200 bg-white">
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
              >
                <X size={18} />
                بستن
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrecottagesByNumber;
